import { bubbleIcons, useTavusGenerateEmbedCode } from '../../model';
import {
  EPersonaIntegrationMode,
  ITavusPersona,
  ITavusPersonaFormik,
  tavusPersonaStore
} from 'entities/tavus/persona';
import { FilledButton, Loader } from 'shared/ui';
import Divider from 'components/Divider';
import { PROFILE_ROUTE, showMessage, showSuccessMessage } from 'shared/model';
import { activeClasses, cn } from 'shared/lib';
import { FC, useEffect, useMemo, useState } from 'react';
import { FormikProps } from 'formik';
import { apiKeyStore } from 'entities/apiKey';
import { EServiceName } from 'entities/apiKey/api';
import SelectInput from 'components/shared/SelectInput';
import { FiInfo } from 'react-icons/fi';

interface IProps {
  formik: FormikProps<ITavusPersonaFormik>;
  persona: ITavusPersona;
}
export const Integrate: FC<IProps> = ({ formik, persona }) => {
  const {
    fetchApiKeys,
    createApiKey,
    isLoadingList,
    apiKeys,
    isLoadingCreate
  } = apiKeyStore((state) => ({
    fetchApiKeys: state.fetchAll,
    createApiKey: state.create,
    apiKeys: state.apiKeys,
    isLoadingList: state.isLoadingList,
    isLoadingCreate: state.isLoadingCreate
  }));
  const filteredApiKeys = useMemo(
    () =>
      apiKeys.filter((apiKey) => apiKey.serviceName === EServiceName.WIDGETS),
    [apiKeys]
  );
  const windowFields = formik.values.widget;

  const bubbleIcon =
    Object.entries(bubbleIcons).find(([key, value]) => {
      if (value.variant === formik.values.widget.bubble_icon_variant)
        return true;
      return false;
    }) || 'first';
  const { embedCode, widgetCode } = useTavusGenerateEmbedCode({
    internalKey: filteredApiKeys.find(
      (item) => item.id === windowFields.api_key_id
    )?.encryptedKey,
    personaId: persona.id,
    type: windowFields.type,
    bubbleBackgroundColor: formik.values.widget.bubble_background_color!,
    bubbleIconColor: formik.values.widget.bubble_icon_color!,
    bubbleIconCustom: formik.values.widget.bubble_icon_custom,
    bubbleIconUrl: formik.values.widget.bubble_icon_url,
    bubbleIconVariant: bubbleIcon[0],
    darkMode: windowFields.dark_mode,
    bubbleIconSide: formik.values.widget.bubble_icon_side,
    customWidgetTrigger: formik.values.widget.custom_widget_trigger
  });

  const [fetchedApiKeys, setFetchedApiKeys] = useState(false);

  useEffect(() => {
    fetchApiKeys().then(() => {
      setFetchedApiKeys(true);
    });
  }, []);

  useEffect(() => {
    if (!fetchedApiKeys) return;

    if (!filteredApiKeys.length) {
      createApiKey({ serviceName: EServiceName.WIDGETS }).then((response) => {
        if (response) {
          showMessage(
            'A new API key has been generated. Please save your changes to use it.'
          );
          formik.setFieldValue('widget.api_key_id', response.id);
        }
      });
    } else {
      const selectedKey = filteredApiKeys.find(
        (key) => key.id === windowFields.api_key_id
      );

      if (!windowFields.api_key_id || !selectedKey) {
        if (windowFields.api_key_id && !selectedKey) {
          showMessage(
            'A previously selected API key is no longer available. The first available key has been set.'
          );
        }

        formik.setFieldValue('widget.api_key_id', filteredApiKeys[0].id);
      }
    }
  }, [fetchedApiKeys, filteredApiKeys]);

  return (
    <>
      <div className="ag-border-sky-300 ag-border ag-rounded-xl ag-flex ag-items-center ag-p-3 ag-bg-sky-50 ag-gap-3">
        <div className="ag-rounded-full ag-bg-sky-100 ag-text-lg ag-text-sky-300 ag-flex ag-items-center ag-justify-center ag-p-2">
          <FiInfo />
        </div>
        <div className="ag-text-sky-700 ag-text-sm ag-font-medium">
          When users chat with your Video Agent, it will deduct credits from
          your account.
        </div>
      </div>
      <div>
        <div className="ag-mb-2 ag-text-neutral-950 ag-font-medium">
          How to install the widget
        </div>
        <Divider />
      </div>
      {isLoadingList || isLoadingCreate ? (
        <div className="ag-flex ag-items-center ag-justify-center ag-h-8">
          <Loader size={20} />
        </div>
      ) : (
        <>
          <div className="ag-flex ag-flex-col ag-gap-1">
            <span className="ag-text-neutral-700 ag-font-medium">
              Select API Key
            </span>
            <SelectInput
              name="select-api-key"
              options={filteredApiKeys.map((apiKey) => apiKey.encryptedKey!)}
              value={
                filteredApiKeys.find(
                  (item) => item.id === formik.values.widget.api_key_id
                )?.encryptedKey || ''
              }
              onChange={(value) =>
                formik.setFieldValue(
                  'widget.api_key_id',
                  filteredApiKeys.find((item) => item.encryptedKey === value)
                    ?.id
                )
              }
              optionsWrapperClassName="!ag-bg-white !ag-border-neutral-200 ag-left-auto ag-right-0"
              className="ag-h-8 !ag-bg-white !ag-border-neutral-200 !ag-text-neutral-700"
              wrapperClassName="!ag-bg-white !ag-text-neutral-700 !ag-border-neutral-200"
              optionsClassName="ag-text-neutral-700 ag-border-neutral-200 ag-text-sm ag-py-1 hover:ag-bg-neutral-200/80 dark:hover:ag-bg-[#262626]/80"
            />
            <a
              href={PROFILE_ROUTE + '#api'}
              target="_blank"
              rel="noreferrer"
              className="ag-text-primary-600 hover:ag-underline ag-text-sm"
            >
              Manage API Keys
            </a>
          </div>
          <Divider />
          <>
            <div>
              <div className="ag-text-sm ag-mb-1.5">
                Paste this code inside a ‘head’ tag
              </div>
              <div className="ag-border ag-border-neutral-300 ag-rounded-md ag-p-3 ag-mb-1.5 ag-overflow-auto ag-max-h-32">
                {widgetCode[0]}
              </div>
              <FilledButton
                className={cn(
                  'ag-py-1.5 ag-px-2 ag-font-semibold ag-leading-6 ag-border ag-border-secondary disabled:ag-bg-primary-200 disabled:ag-border-primary-200 ag-text-sm ag-mb-6 hover:ag-bg-primary-700',
                  activeClasses
                )}
                onClick={() =>
                  navigator.clipboard
                    .writeText(widgetCode[0])
                    .then(() =>
                      showSuccessMessage(
                        'The head part of widget code has been copied'
                      )
                    )
                }
              >
                Copy
              </FilledButton>
            </div>
            <div>
              <div className="ag-text-sm ag-mb-1.5">
                Paste this code before bod tag
              </div>
              <div className="ag-border ag-border-neutral-300 ag-rounded-md ag-p-3 ag-mb-1.5 ag-overflow-auto ag-max-h-32">
                {widgetCode[1]}
              </div>
              <FilledButton
                className={cn(
                  'ag-py-1.5 ag-px-2 ag-font-semibold ag-leading-6 ag-border ag-border-secondary disabled:ag-bg-primary-200 disabled:ag-border-primary-200 ag-text-sm  hover:ag-bg-primary-700',
                  activeClasses
                )}
                onClick={() =>
                  navigator.clipboard
                    .writeText(widgetCode[1])
                    .then(() =>
                      showSuccessMessage(
                        'The head part of widget code has been copied'
                      )
                    )
                }
              >
                Copy
              </FilledButton>
            </div>
          </>
        </>
      )}
    </>
  );
};
